/**
 * Google OAuth Service with Persistent Authentication via Backend
 *
 * Uses redirect-based OAuth flow through the backend (/api/auth/*).
 * The backend stores refresh_token in Redis and handles silent token renewal.
 * The frontend only holds the short-lived access_token in memory/sessionStorage (cleared on tab close — #88).
 *
 * GIS is still loaded for requestAdditionalScope() (incremental auth).
 */

import { logger } from '../utils/logger';
import { safeSetItem } from '../utils/safeLocalStorage';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const _SCOPES = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.appdata https://www.googleapis.com/auth/drive.install https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/tasks https://www.googleapis.com/auth/calendar.events';

// localStorage keys
const STORAGE_KEY_ACCESS_TOKEN = 'bigwa_google_access_token';
const STORAGE_KEY_TOKEN_EXPIRY = 'bigwa_google_token_expiry';
const STORAGE_KEY_USER = 'bigwa_user';

declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: Record<string, unknown>) => { requestAccessToken: (opts?: Record<string, unknown>) => void };
        };
      };
    };
  }
}

class GoogleAuthService {
  private accessToken: string | null = null;
  private tokenExpiry: number | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private initPromise: Promise<void> | null = null;
  private gisLoaded = false;
  private refreshPromise: Promise<string | null> | null = null;

  /**
   * Load Google Identity Services (GIS) — only needed for requestAdditionalScope
   */
  private async loadGIS(): Promise<void> {
    if (this.gisLoaded || window.google?.accounts) {
      this.gisLoaded = true;
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        this.gisLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.body.appendChild(script);
    });
  }

  /**
   * Initialize — restore session from backend or localStorage.
   * Does NOT open popup or redirect. Called once on app boot.
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    // 1. Restore token from localStorage (instant, synchronous)
    this.restoreTokenFromStorage();

    // 2. If we have a valid token already, just schedule refresh
    if (this.hasValidToken()) {
      this.scheduleRefresh(this.tokenExpiry!);
      // Load GIS in background (non-blocking) for incremental scopes
      this.loadGIS().catch(() => {});
      return;
    }

    // 3. No valid local token — try to restore from backend session (cookie-based)
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.accessToken) {
          this.accessToken = data.accessToken;
          this.tokenExpiry = data.tokenExpiresAt;
          this.saveTokenToStorage();
          this.scheduleRefresh(data.tokenExpiresAt);

          // Also save user info if present
          if (data.user) {
            safeSetItem(STORAGE_KEY_USER, JSON.stringify({
              name: data.user.name,
              email: data.user.email,
              picture: data.user.picture,
            }));
          }

        }
      }
    } catch (error) {
      logger.warn('GoogleAuth', 'Failed to check backend session:', error);
    }

    // Load GIS in background for incremental scopes
    this.loadGIS().catch(() => {});
  }

  /**
   * Restore token from sessionStorage (synchronous, no network)
   */
  private restoreTokenFromStorage(): void {
    try {
      const storedToken = sessionStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
      const storedExpiry = sessionStorage.getItem(STORAGE_KEY_TOKEN_EXPIRY);

      if (storedToken && storedExpiry) {
        const expiry = parseInt(storedExpiry, 10);
        const now = Date.now();

        // Valid if not expired (1 min buffer)
        if (now < expiry - 60_000) {
          this.accessToken = storedToken;
          this.tokenExpiry = expiry;
        } else {
          this.clearStoredToken();
        }
      }
    } catch (error) {
      logger.warn('GoogleAuth', 'Error restoring token:', error);
    }
  }

  /**
   * Save token to sessionStorage (cleared when tab closes — security #88)
   */
  private saveTokenToStorage(): void {
    try {
      if (this.accessToken && this.tokenExpiry) {
        sessionStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, this.accessToken);
        sessionStorage.setItem(STORAGE_KEY_TOKEN_EXPIRY, this.tokenExpiry.toString());
      }
    } catch (error) {
      logger.warn('GoogleAuth', 'Error saving token:', error);
    }
  }

  /**
   * Clear stored tokens (memory + sessionStorage)
   */
  private clearStoredToken(): void {
    sessionStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEY_TOKEN_EXPIRY);
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Schedule a silent refresh 5 minutes before the token expires.
   */
  private scheduleRefresh(expiresAt: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const refreshIn = expiresAt - Date.now() - 5 * 60 * 1000; // 5 min before expiry

    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(() => {
        this.ensureFreshToken().catch((err) => {
          logger.error('GoogleAuth', 'Scheduled refresh failed:', err);
        });
      }, refreshIn);
    } else {
      // Already expiring soon — refresh now
      this.ensureFreshToken().catch((err) => {
        logger.error('GoogleAuth', 'Immediate refresh failed:', err);
      });
    }
  }

  /**
   * Ensure we have a fresh access token by calling POST /api/auth/refresh.
   * Returns the new token or null if refresh failed.
   * Deduplicates concurrent calls.
   */
  async ensureFreshToken(): Promise<string | null> {
    // If token is still valid with 5 min buffer, return it
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry - 5 * 60_000) {
      return this.accessToken;
    }

    // Deduplicate concurrent refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async _doRefresh(): Promise<string | null> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        logger.error('GoogleAuth', 'Refresh failed:', data.code || response.status);
        this.handleSessionExpired();
        return null;
      }

      const data = await response.json();
      this.accessToken = data.accessToken;
      this.tokenExpiry = data.expiresAt;
      this.saveTokenToStorage();
      this.scheduleRefresh(data.expiresAt);

      // Update user info if returned
      if (data.user) {
        safeSetItem(STORAGE_KEY_USER, JSON.stringify({
          name: data.user.name,
          email: data.user.email,
          picture: data.user.picture,
        }));
      }

      return this.accessToken;
    } catch (error) {
      logger.error('GoogleAuth', 'Refresh request failed:', error);
      return null;
    }
  }

  /**
   * Set token received from auth callback (called by authStore after redirect).
   */
  setToken(accessToken: string, expiresAt: number): void {
    this.accessToken = accessToken;
    this.tokenExpiry = expiresAt;
    this.saveTokenToStorage();
    this.scheduleRefresh(expiresAt);
  }

  /**
   * Get the current access token synchronously.
   * Returns null if token is expired or missing.
   * Use ensureFreshToken() for async refresh-and-get.
   *
   * AUDIT NOTE (M2): This method is intentionally synchronous and safe.
   * It only reads from in-memory cache and localStorage — no network calls.
   * All callers that need a fresh token should use getTokenOrRefresh() instead.
   */
  getAccessToken(): string | null {
    // Check in-memory token
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry - 60_000) {
      return this.accessToken;
    }

    // Try localStorage
    if (!this.accessToken) {
      this.restoreTokenFromStorage();
      if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry - 60_000) {
        return this.accessToken;
      }
    }

    // Token expired or missing
    return null;
  }

  /**
   * Check if we have a valid (non-expired) token
   */
  hasValidToken(): boolean {
    return this.getAccessToken() !== null;
  }

  /**
   * Initiate login by redirecting to /api/auth/login
   */
  login(returnTo?: string): void {
    const loginUrl = new URL('/api/auth/login', window.location.origin);
    if (returnTo) {
      loginUrl.searchParams.set('returnTo', returnTo);
    }
    window.location.href = loginUrl.toString();
  }

  /**
   * Sign out — clear local state and redirect to backend logout
   */
  /**
   * Handle definitive session expiry: save context, toast, redirect after 30s.
   * NEVER call signOut() directly — always go through this method.
   */
  private handleSessionExpired(): void {
    // 1. Save recovery context
    try {
      sessionStorage.setItem('bigwa_session_recovery', JSON.stringify({
        timestamp: Date.now(),
        url: window.location.href,
      }));
    } catch {
      // sessionStorage unavailable — continue anyway
    }

    // 2. Clear token
    this.clearStoredToken();

    // 3. Persistent toast — does not auto-dismiss
    // Import dynamically to avoid circular dependency
    import('react-hot-toast').then(({ default: toast }) => {
      toast.error(
        'Sua sessão expirou. Salve qualquer trabalho pendente. Redirecionando para login em 30 segundos...',
        { duration: Infinity }
      );
    }).catch(() => {
      // toast unavailable — redirect will happen anyway
    });

    // 4. Redirect after 30s (time to save work)
    setTimeout(() => {
      window.location.href = '/api/auth/logout';
    }, 30000);
  }

  signOut(): void {
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Clear tokens
    this.clearStoredToken();
    localStorage.removeItem(STORAGE_KEY_USER);
    sessionStorage.clear();

    // Redirect to backend logout (clears cookie + Redis session)
    window.location.href = '/api/auth/logout';
  }

  /**
   * Request additional scope (incremental authorization) via GIS popup.
   * Still uses GIS because incremental scopes need an interactive popup.
   */
  async requestAdditionalScope(scope: string): Promise<boolean> {
    // Ensure GIS is loaded
    if (!this.gisLoaded) {
      await this.loadGIS();
    }

    if (!window.google?.accounts?.oauth2) {
      logger.error('GoogleAuth', 'GIS not available for incremental scope');
      return false;
    }

    return new Promise((resolve) => {
      try {
        const incrementalClient = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: scope,
          callback: (response: { error?: string; access_token?: string; expires_in?: number }) => {
            if (response.error) {
              logger.error('GoogleAuth', 'Error adding scope:', response.error);
              resolve(false);
              return;
            }

            // Update token with the new one that includes additional scopes
            this.accessToken = response.access_token ?? null;
            const expiresIn = response.expires_in ?? 3600;
            this.tokenExpiry = Date.now() + (expiresIn * 1000);
            this.saveTokenToStorage();
            this.scheduleRefresh(this.tokenExpiry);

            resolve(true);
          },
        });

        incrementalClient.requestAccessToken({
          prompt: '',
          include_granted_scopes: true,
        });
      } catch (error) {
        logger.error('GoogleAuth', 'Error:', error);
        resolve(false);
      }
    });
  }

  /**
   * Get user info — from localStorage cache first, then from Google API
   */
  async getUserInfo(): Promise<{
    name: string;
    email: string;
    picture: string;
  } | null> {
    // Try cache first
    try {
      const cached = localStorage.getItem(STORAGE_KEY_USER);
      if (cached) {
        const user = JSON.parse(cached);
        if (user.email && user.name) {
          return user;
        }
      }
    } catch (_e) {
      // Ignore parse error
    }

    // Fetch from Google API
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) return null;

      const data = await response.json();
      const userInfo = {
        name: data.name || data.given_name || 'Usuário',
        email: data.email || '',
        picture: data.picture || '',
      };

      safeSetItem(STORAGE_KEY_USER, JSON.stringify(userInfo));
      return userInfo;
    } catch (error) {
      logger.error('GoogleAuth', 'Error fetching userinfo:', error);
      return null;
    }
  }

  /**
   * Synchronous version of getUserInfo (cache only)
   */
  getUserInfoSync(): { name: string; email: string; picture: string } | null {
    try {
      const cached = localStorage.getItem(STORAGE_KEY_USER);
      if (cached) return JSON.parse(cached);
    } catch (_e) {
      // Ignore
    }
    return null;
  }
}

export const googleAuth = new GoogleAuthService();

/**
 * Get a valid access token, refreshing silently if expired.
 * Use this instead of googleAuth.getAccessToken() in async contexts.
 * Throws if refresh fails — caller should handle the error.
 */
export async function getTokenOrRefresh(): Promise<string> {
  const token = googleAuth.getAccessToken();
  if (token) return token;

  try {
    const freshToken = await googleAuth.ensureFreshToken();
    if (freshToken) return freshToken;
  } catch (error) {
    logger.error('getTokenOrRefresh', 'refresh failed', error);
  }

  const { default: toast } = await import('react-hot-toast');
  toast.error('Sessão expirada. Faça login novamente.');
  throw new Error('Token refresh failed');
}
